/**
 * File (Vault) WRITE endpoints — request schemas (Pattern B).
 *
 * These schemas ARE THE FIELD WHITELIST for `public.files`. Both are
 * `.strict()` on purpose: zod v3 silently STRIPS unknown keys otherwise, so a
 * body carrying `created_by` or `organization_id` would 200 with the extra key
 * quietly dropped instead of 400ing — and the whitelist would then be a
 * convention nobody can test. `.strict()` makes an attempt to widen the
 * accepted field set a visible failure.
 *
 * `public.files` writes are backend-only as of
 * `20260826090200_lockdown_files_writes.sql`: three RESTRICTIVE
 * `verify_backend_token()` policies on INSERT/UPDATE/DELETE, ANDed with the
 * PERMISSIVE org-membership policies that keep doing the authorization. The
 * handlers in `apps/api/src/routes/platform/files.ts` write through the per-request user
 * client — which signs `x-backend-auth` — so org RLS is still the row-level
 * boundary AND the gate passes there and only there.
 *
 * That makes this whitelist load-bearing rather than advisory: the schema is
 * the only thing standing between a caller and a field the DB would happily
 * accept from the backend's own trusted connection. See that file's header for
 * the full Pattern B classification and docs/BACKEND_API.md → "Pattern B".
 */
import { z } from "zod";
/**
 * POST /orgs/:orgId/files body.
 *
 * DELIBERATELY ABSENT — every one of these is server-pinned in the handler and
 * must never be settable from the wire:
 *   - `organization_id`   — comes from the validated `:orgId` route param.
 *   - `created_by`        — comes from the verified token (`c.get("userId")`);
 *                           the INSERT policy's `WITH CHECK (created_by =
 *                           auth.uid())` pins it a second time in the DB.
 *   - `code`              — assigned by the `auto_generate_file_code` BEFORE
 *                           INSERT trigger (the handler passes '').
 *   - `status`            — always 'active' on create; changed only via PATCH.
 *   - `is_generated_report` / `report_type` — reserved for the report-export
 *                           writers, not for user uploads.
 *
 * `file_type` is an OPEN STRING VOCABULARY, never an enum: the Vault upload
 * dialog's eight tokens are only one writer — the attachment hooks push their
 * own `attachment_type` and the inbox edge function writes another token
 * (see `20260825120000_file_index_view.sql` note 3). Constraining it here would
 * start rejecting legitimate rows.
 *
 * `tags` is NOT a column — the handler folds it into `metadata.tags`, which is
 * what the `file_index` view projects.
 */
export const createFileSchema = z
    .object({
    name: z.string().min(1).max(255),
    file_type: z.string().min(1),
    file_path: z.string().min(1),
    file_size: z.number().int().nonnegative().optional(),
    content_type: z.string().optional(),
    project_id: z.string().uuid().nullable().optional(),
    // BOUNDED on both axes. `tags` is unvalidated free text that lands in
    // metadata.tags and is concatenated into file_index.search_text, which is
    // recomputed for the whole org on every Vault index fetch — so an
    // unbounded array here is an unbounded read cost for everyone in the org,
    // not just the uploader. 64 chars is generous for a label; 50 tags is far
    // past any real use. Empty/whitespace tags are rejected rather than stored
    // (TagInput already trims, but the schema must not depend on the UI).
    tags: z.array(z.string().trim().min(1).max(64)).max(50).optional(),
})
    .strict();
/**
 * PATCH /orgs/:orgId/files/:fileId body — any subset, at least one field.
 * Serves the Vault edit dialog (name / project / tags) AND the RecentReports
 * soft-delete (`status: 'deleted'`).
 *
 * `file_path` is deliberately omitted and is the sharpest exclusion on this
 * table: accepting it would let a member repoint an owned row at ANOTHER org's
 * storage object, after which the id-keyed signed-URL mint would happily sign
 * it. The path is written once, at upload.
 *
 * Also absent: `organization_id`, `created_by`, `code` (immutable identity),
 * `file_size`, `content_type` (properties of the stored object — they change
 * only by re-uploading), `is_generated_report`, `report_type`, and `metadata`
 * itself — the raw jsonb blob stays unwritable from the wire, because other
 * subsystems keep their own keys in it (the inbox pipeline's `source`, report
 * generation's own keys) and a whole-object body would silently destroy them.
 * What the endpoint DOES offer is `tags`: a TYPED `string[]` face of
 * `metadata.tags`, which `public.update_vault_file` MERGES into the existing
 * blob (`metadata || jsonb_build_object('tags', …)`) so every sibling key
 * survives the edit. Typed-and-merged is the whole reason the write goes
 * through that RPC — PostgREST's `.update()` can only SET a column, so the
 * merge is inexpressible through it.
 *
 * NOTE: `public.files.status` has NO CHECK constraint — this enum is the ONLY
 * guard against an arbitrary status string reaching the column.
 */
export const updateFileSchema = z
    .object({
    name: z.string().min(1).max(255),
    status: z.enum(["active", "deleted"]),
    project_id: z.string().uuid().nullable(),
    // NOT a column — folded into `metadata.tags` by the RPC, which is what the
    // `file_index` view projects (and what feeds its `search_text`). An empty
    // array is a real edit (clear every tag), never "absent": `.partial()`
    // distinguishes the two by key presence, and so does the RPC's jsonb `?`.
    // Same bounds as createFileSchema — see the note there. They must match:
    // a tag that can be created but not preserved through an edit would be
    // silently dropped the first time the user touched any other field.
    tags: z.array(z.string().trim().min(1).max(64)).max(50),
})
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, {
    message: "At least one field must be provided",
});
/**
 * POST /orgs/:orgId/attachments body — ONE endpoint for every attachment
 * surface (activity / ledger transaction / AP invoice / AR invoice / asset /
 * person).
 *
 * WHY ONE ENDPOINT
 *   The FILE half of an attachment write is byte-identical across the four
 *   surfaces; only the junction table and one parent column differ. Four
 *   endpoints would quadruple the atomic-proc call, the exactly-one-parent
 *   invariant and the compensating logic for no gain.
 *
 * WHY A DISCRIMINATED UNION ON `parent`
 *   It makes "exactly one parent" UNREPRESENTABLE ON THE WIRE rather than
 *   merely validated: there is no body shape that carries two parent ids and
 *   still parses. `public.create_file_attachment` re-checks the invariant
 *   server-side anyway (defence in depth) — but the wire contract is what stops
 *   a caller from ever having to be told.
 *
 * EVERY OBJECT HERE IS `.strict()`, for the same reason stated at the top of
 * this file: zod v3 STRIPS unknown keys by default, so a body carrying both
 * `activity_id` and `asset_id` inside one union member would parse 200 with the
 * extra id silently dropped instead of 400ing — and the union's guarantee would
 * be folklore rather than a testable contract.
 *
 * `attachment_type` spans ALL THREE families in one enum (transaction
 * attachments: receipt/invoice/supporting_doc/activity; asset attachments:
 * purchase_receipt/manual/warranty/photo; person attachments: contract/
 * identity/cv/certificate/other). The kind↔type matrix is deliberately NOT
 * restated here: `create_file_attachment` raises `22023` on a family mismatch
 * (e.g. an asset parent with 'receipt'), and duplicating the matrix in Zod
 * would give it two homes that can drift.
 *
 * DELIBERATELY ABSENT, exactly as in `createFileSchema`:
 *   - `organization_id` — derived FROM THE PARENT inside the proc, under the
 *                         caller's own RLS, so the file always lands in the
 *                         same org as the thing it is attached to.
 *   - `created_by`      — `auth.uid()` on BOTH inserted rows; the SQL signature
 *                         has no parameter for it at all.
 *   - `code` / `status` — trigger-assigned / server-pinned, as on create.
 *   - `file_type`       — RESOLVED by the handler, not sent: 'activity' for an
 *                         activity parent, the `attachment_type` otherwise
 *                         (a person parent included).
 */
export const createFileAttachmentSchema = z
    .object({
    parent: z.discriminatedUnion("kind", [
        // NOTE: every member must stay a PLAIN ZodObject — a `.refine()` or
        // `.transform()` on a member makes it a ZodEffects, which
        // z.discriminatedUnion rejects at schema-construction time (same trap
        // noted in schemas/clearing.ts and schemas/pdf.ts).
        z.object({ kind: z.literal("activity"), activity_id: z.string().uuid() }).strict(),
        z
            .object({
            kind: z.literal("ledger_transaction"),
            ledger_transaction_id: z.string().uuid(),
        })
            .strict(),
        z.object({ kind: z.literal("ap_invoice"), ap_invoice_id: z.string().uuid() }).strict(),
        // An AR invoice (20261101120000): EVERY new AR attachment keys on the
        // invoice, draft or issued — a draft has no journal to hang it on, and
        // the link must survive Issue unchanged. Legacy AR attachments stay on
        // the invoice's journal (`ledger_transaction`).
        z.object({ kind: z.literal("ar_invoice"), ar_invoice_id: z.string().uuid() }).strict(),
        z.object({ kind: z.literal("asset"), asset_id: z.string().uuid() }).strict(),
        // person_attachments. `visibility` is optional: absent, the proc
        // defaults it to admins (DEFAULT_PERSON_ATTACHMENT_VISIBILITY). The
        // proc refuses `p_visibility` for every other parent (22023), so it
        // lives ONLY on this member — the handler forwards it only from here.
        z
            .object({
            kind: z.literal("person"),
            person_id: z.string().uuid(),
            visibility: z.enum(["admins", "members"]).optional(),
        })
            .strict(),
    ]),
    attachment_type: z.enum([
        // transaction_attachments family
        "receipt",
        "invoice",
        "supporting_doc",
        "activity",
        // asset_attachments family
        "purchase_receipt",
        "manual",
        "warranty",
        "photo",
        // person_attachments family (PERSON_ATTACHMENT_TYPES)
        "contract",
        "identity",
        "cv",
        "certificate",
        "other",
    ]),
    // Same field shapes as `createFileSchema` above — the storage object is
    // committed by the browser first, and this is its description.
    file: z
        .object({
        name: z.string().min(1).max(255),
        file_path: z.string().min(1),
        file_size: z.number().int().nonnegative().optional(),
        content_type: z.string().optional(),
    })
        .strict(),
})
    .strict();
/**
 * The attachment parents that are ACCOUNTING records — a journal, an AP bill
 * or an AR invoice. Attaching to (or detaching from) one needs the
 * `accounting` scope, and its file must live under an accounting folder
 * (`ACCOUNTING_FILE_FOLDERS`). The SQL twins are the `file_attachment_kind`
 * values and `create_file_attachment`'s folder pin.
 */
export const ACCOUNTING_ATTACHMENT_PARENT_KINDS = [
    "ledger_transaction",
    "ap_invoice",
    "ar_invoice",
];
/** Is `kind` (a parent kind, or a `file_attachment_kind` result) an accounting record? */
export function isAccountingAttachmentKind(kind) {
    return kind != null && ACCOUNTING_ATTACHMENT_PARENT_KINDS.includes(kind);
}
// ---------------------------------------------------------------------------
// Person attachments (`public.person_attachments`)
// ---------------------------------------------------------------------------
/** The `person_attachments.attachment_type` CHECK vocabulary, in display order. */
export const PERSON_ATTACHMENT_TYPES = ["contract", "identity", "cv", "certificate", "other"];
/**
 * The `person_attachments.visibility` CHECK vocabulary. `admins` hides the
 * link, the `files` row and the storage object from non-admin members (RLS on
 * all three); `members` is visible to every org member.
 */
export const PERSON_ATTACHMENT_VISIBILITY = ["admins", "members"];
/**
 * The visibility a new document gets when none is chosen — admins-only for
 * every type (least privilege; an admin can open it up later). The TS twin of
 * `create_file_attachment`'s default.
 */
export const DEFAULT_PERSON_ATTACHMENT_VISIBILITY = "admins";
/**
 * PATCH /api/v1/people/orgs/:orgId/people/:personId/attachments/:attachmentId
 * body. Only the visibility is editable; `.strict()` makes any other key a 400.
 */
export const setPersonAttachmentVisibilitySchema = z
    .object({ visibility: z.enum(PERSON_ATTACHMENT_VISIBILITY) })
    .strict();
//# sourceMappingURL=files.js.map